import { z } from "zod";

const pickemsCompetitor = z.object({
  abbreviation: z.string().nullish(),
  id: z.string(),
  name: z.string(),
  type: z.enum(["FRANCHISE", "PLAYER"]),
  iconUri: z.string().optional(),
});

const pickemsMarketOutcome = z.object({
  competitor: pickemsCompetitor.nullish(), // if the outcome type is DRAW we won't have competitor data
  competitorId: z.string().nullish(), // if the outcome type is DRAW we won't have competitor data
  id: z.string(),
  points: z.number(),
  result: z.enum(["WIN", "LOSE", "UNDECIDED", "VOID"]),
  type: z.enum([
    "FRANCHISE",
    "FRANCHISE_HOME",
    "FRANCHISE_AWAY",
    "PLAYER",
    "PLAYER_HOME",
    "PLAYER_AWAY",
    "DRAW",
  ]),
});

const pickemsMarketEvent = z.object({
  attributes: z.union([
    z.object({
      categoryName: z.string(),
      matchMode: z.string(),
      tournamentName: z.string(),
    }),
    z.record(z.string(), z.unknown()),
  ]),
  competitors: z.record(z.string(), z.unknown()),
  eventId: z.string(),
  eventName: z.string(),
  eventType: z.enum(["MATCH", "RACE", "SEASON", "TOURNAMENT"]),
  hub: z.enum(["esports", "sports", "racing"]),
  id: z.string(),
  scheduledStartTime: z.iso.datetime(),
  sport: z.string(),
  streamUri: z.string().optional(),
  tournamentId: z.string(),
});

const pickemsMarket = z.object({
  bettingMarketId: z.string(),
  event: pickemsMarketEvent,
  eventReferenceId: z.string(),
  id: z.string(),
  outcomes: z.record(z.string(), pickemsMarketOutcome),
  status: z.enum([
    "ACTIVE",
    "SUSPENDED",
    "HANDED_OVER",
    "SETTLED",
    "CANCELLED",
    "DEACTIVATED",
    "UNAVAILABLE",
    "INVALID",
  ]),
  scheduledStartTime: z.iso.datetime(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});

const pickemsContestMarketCounts = z.object({
  active: z.number(),
  total: z.number(),
});

const pickemsContestExchangeRates = z.object({
  AUD: z.number().optional(),
  CAD: z.number().optional(),
  EUR: z.number().optional(),
  NZD: z.number().optional(),
});

const pickemsEntrySelectionEvent = z.object({
  eventId: z.string(),
  hub: z.string(),
  eventName: z.string(),
  tournamentId: z.string().optional(),
  sport: z.string(),
  scheduledStartTime: z.iso.datetime(),
  attributes: z.union([
    z.object({
      categoryName: z.string(),
      matchMode: z.string(),
      tournamentName: z.string(),
    }),
    z.record(z.string(), z.unknown()),
  ]),
  eventType: z.string(),
  id: z.string(),
});

const pickemsEntrySelectionsMapItem = z.object({
  outcomeId: z.string(),
  event: pickemsEntrySelectionEvent,
  outcomeName: z.string(),
  result: z.string(),
  competitor: pickemsCompetitor,
  competitorId: z.string(),
  marketId: z.string(),
  outcomeType: z.string(),
  points: z.number(),
  marketStatus: z.string().optional(),
});

const pickemsEntryData = z.object({
  entryId: z.number(),
  currency: z.string(),
  paidOut: z.number(),
  status: z.enum(["WON", "PLACED", "CANCELLED", "LOST", "LIVE"]),
  points: z.number(),
  place: z.number().optional(),
  contestNumber: z.number(),
  createdAt: z.iso.datetime(),
  selectionsMap: z.record(z.string(), pickemsEntrySelectionsMapItem),
  contestTitle: z.string(),
});

const pickemsContestLeaderboardEntryUser = z.object({
  username: z.string(),
  id: z.string(),
  avatarUri: z.string().optional(),
});

const pickemsContestLeaderboardEntry = z.object({
  id: z.string(),
  paidOut: z.number(),
  place: z.number().optional(),
  points: z.number(),
  selectionCount: z.number(),
  status: z.enum(["PENDING", "LIVE", "CANCELLED", "COMPLETED"]),
  totalSelectedPoints: z.number(),
  winningSelectionCount: z.number(),
  user: pickemsContestLeaderboardEntryUser,
});

const pickemsContestEntries = z.object({
  live: z.number(),
  total: z.number(),
});

const pickemsContest = z.object({
  cardImageUri: z.string().optional(),
  category: z.string(),
  contestNumber: z.number(),
  description: z.string().optional(),
  entries: pickemsContestEntries,
  exchangeRates: pickemsContestExchangeRates.optional(),
  marketCounts: pickemsContestMarketCounts,
  moreMarketsExpected: z.boolean(),
  prizePool: z.number().array(),
  rules: z.string().optional(),
  scheduledStartTime: z.iso.datetime().optional(),
  sports: z.string().array(),
  status: z.enum(["PENDING", "LIVE", "CANCELLED", "COMPLETED"]),
  title: z.string(),
});

type PickemsMarket = z.infer<typeof pickemsMarket>;
type PickemsContest = z.infer<typeof pickemsContest>;
type PickemsContestLeaderboardEntry = z.infer<
  typeof pickemsContestLeaderboardEntry
>;

export type { PickemsMarket, PickemsContest, PickemsContestLeaderboardEntry };

export {
  pickemsMarket,
  pickemsContest,
  pickemsContestLeaderboardEntry,
  pickemsEntryData,
};
